import {useEffect} from 'react'
import {Pressable, View} from 'react-native'

import {useSession, useSessionApi} from '#/state/session'
import {useSetImpersonationOffset} from '#/state/shell/impersonation-offset'
import {atoms as a} from '#/alf'
import {Text} from '#/components/Typography'

/**
 * A persistent, unmissable bar shown ONLY while an operator is impersonating a
 * user ("assume user" from the admin console). The impersonation session is
 * ephemeral — held in memory for this tab only, never persisted — so there is
 * nothing to clean up: Exit just drops it, and closing/reloading the tab does
 * the same. Web-only (the `.web.tsx` extension keeps it off native).
 *
 * Alongside Exit, the bar offers jump-links back to the operator console's
 * marketing surfaces (Studio/Brand) for the SAME brand, so the operator can go
 * from the user's-eye view straight to composing/scheduling or theming. The
 * links open the console in a NEW tab (different origin, and it keeps this
 * ephemeral session alive) and only render for surfaces the deployment enables.
 */

function openConsole(url: string) {
  if (typeof window !== 'undefined') {
    window.open(url, '_blank', 'noopener,noreferrer')
  }
}

function BannerButton({
  label,
  onPress,
  accessibilityHint,
}: {
  label: string
  onPress: () => void
  accessibilityHint?: string
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityHint={accessibilityHint}
      onPress={onPress}
      style={[
        a.rounded_sm,
        a.px_md,
        {paddingVertical: 6, backgroundColor: 'rgba(255,255,255,0.2)'},
      ]}>
      <Text style={[a.font_bold, a.text_sm, {color: '#fff'}]}>{label}</Text>
    </Pressable>
  )
}

export function ImpersonationBanner() {
  const {isImpersonating, currentAccount, impersonationLinks} = useSession()
  const {exitImpersonation} = useSessionApi()
  const setOffset = useSetImpersonationOffset()

  // Reset the chrome offset whenever the banner leaves (Exit / session end) —
  // onLayout only fires while it is mounted.
  useEffect(() => {
    if (!isImpersonating) setOffset(0)
    return () => setOffset(0)
  }, [isImpersonating, setOffset])

  if (!isImpersonating) return null

  // Narrow to locals so the closures below don't need non-null assertions.
  const studioUrl = impersonationLinks?.studio
  const brandUrl = impersonationLinks?.brand

  return (
    <View
      // Sticky at the shell top: the bar occupies real layout space (pushing
      // the page down — never floating over page tools) and stays pinned while
      // scrolling. Chrome that positions itself against the viewport reads
      // useImpersonationOffset() (set from onLayout here) as its `top`.
      onLayout={e => setOffset(e.nativeEvent.layout.height)}
      style={[
        a.flex_row,
        a.flex_wrap,
        a.align_center,
        a.justify_center,
        a.gap_sm,
        a.px_lg,
        a.sticky,
        {
          top: 0,
          paddingVertical: 8,
          backgroundColor: '#b45309', // amber-700 — deliberate, theme-independent
          zIndex: 100,
        },
      ]}
      accessibilityRole="alert">
      <Text style={[a.font_bold, a.text_sm, {color: '#fff'}]}>
        ⚠ Acting as @{currentAccount?.handle ?? 'user'} — this is a live
        session, recorded for the account owner.
      </Text>
      {studioUrl ? (
        <BannerButton
          label="Studio"
          accessibilityHint="Opens the marketing Studio for this brand in the console (new tab)"
          onPress={() => openConsole(studioUrl)}
        />
      ) : null}
      {brandUrl ? (
        <BannerButton
          label="Brand"
          accessibilityHint="Opens the Brand manager for this brand in the console (new tab)"
          onPress={() => openConsole(brandUrl)}
        />
      ) : null}
      <BannerButton
        label="Exit"
        accessibilityHint="Ends the assumed session"
        onPress={exitImpersonation}
      />
    </View>
  )
}
